import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useAgent } from "@/hooks/useAgents";
import apiClient from "@/api/client";
import ChipListEditor from "@/components/ChipListEditor";
import type { Agent } from "@/types";

export default function AgentEdit() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agent, isLoading, error } = useAgent(agentId);

  const [form, setForm] = useState<Partial<Agent>>({});
  const [reason, setReason] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (agent) {
      setForm({
        name: agent.name,
        tagline: agent.tagline,
        role_description: agent.role_description,
        personality: agent.personality,
        communication_style: agent.communication_style,
        goals: agent.goals ?? [],
        skills: agent.skills ?? [],
        model_provider: agent.model_provider,
        model_id: agent.model_id,
        model_config_json: agent.model_config_json ?? {},
        comms_channels: agent.comms_channels ?? [],
        heartbeat_cron: agent.heartbeat_cron,
        is_active: agent.is_active,
      });
    }
  }, [agent]);

  const mutation = useMutation({
    mutationFn: async (data: { form: Partial<Agent>; reason: string }) => {
      const params = data.reason ? `?reason=${encodeURIComponent(data.reason)}` : "";
      const res = await apiClient.put<Agent>(`/agents/${agentId}${params}`, data.form);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      setSnackbar({ open: true, message: "Agent updated successfully", severity: "success" });
    },
    onError: () => {
      setSnackbar({ open: true, message: "Failed to update agent", severity: "error" });
    },
  });

  const handleSave = () => {
    mutation.mutate({ form, reason });
  };

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent) {
    return <Alert severity="error">Agent not found.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/agents/${agentId}`)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Edit {agent.code}: {agent.name}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Left: Identity & Personality */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Identity
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Name"
                  value={form.name ?? ""}
                  onChange={(e) => updateField("name", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Tagline"
                  value={form.tagline ?? ""}
                  onChange={(e) => updateField("tagline", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Role Description"
                  value={form.role_description ?? ""}
                  onChange={(e) => updateField("role_description", e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active ?? true}
                      onChange={(e) => updateField("is_active", e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personality
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Personality"
                  value={form.personality ?? ""}
                  onChange={(e) => updateField("personality", e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                />
                <TextField
                  label="Communication Style"
                  value={form.communication_style ?? ""}
                  onChange={(e) => updateField("communication_style", e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right: Config & Skills */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Model Configuration
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Provider"
                  value={form.model_provider ?? ""}
                  onChange={(e) => updateField("model_provider", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Model ID"
                  value={form.model_id ?? ""}
                  onChange={(e) => updateField("model_id", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Temperature"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 2 }}
                  value={(form.model_config_json as Record<string, unknown>)?.temperature ?? ""}
                  onChange={(e) =>
                    updateField("model_config_json", {
                      ...(form.model_config_json ?? {}),
                      temperature: parseFloat(e.target.value),
                    })
                  }
                  fullWidth
                />
                <TextField
                  label="Max Tokens"
                  type="number"
                  value={(form.model_config_json as Record<string, unknown>)?.max_tokens ?? ""}
                  onChange={(e) =>
                    updateField("model_config_json", {
                      ...(form.model_config_json ?? {}),
                      max_tokens: parseInt(e.target.value, 10),
                    })
                  }
                  fullWidth
                />
                <TextField
                  label="Heartbeat Cron"
                  value={form.heartbeat_cron ?? ""}
                  onChange={(e) => updateField("heartbeat_cron", e.target.value)}
                  fullWidth
                  helperText='e.g. "0 8 * * 1-5" for 8am weekdays'
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <ChipListEditor
                label="Goals"
                values={(form.goals as string[]) ?? []}
                onChange={(v) => updateField("goals", v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <ChipListEditor
                label="Skills"
                values={(form.skills as string[]) ?? []}
                onChange={(v) => updateField("skills", v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Change Reason
              </Typography>
              <TextField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                placeholder="Why are you making this change? (optional but recommended)"
                helperText="Saved to config history for audit trail"
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
